import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef
} from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { AppRole, Profile } from '@/types/database';

/* =========================
   TIPOS
========================= */

interface TipoContextoAutenticacion {
  usuario: User | null;
  sesion: Session | null;
  perfil: Profile | null;
  rol: AppRole | null;
  cargando: boolean;
  registrar: (
    email: string,
    password: string,
    metadata: MetadatosRegistro
  ) => Promise<{ error: Error | null }>;
  iniciarSesion: (
    email: string,
    password: string
  ) => Promise<{ error: Error | null }>;
  cerrarSesion: () => Promise<void>;
  refrescarPerfil: () => Promise<void>;
}

interface MetadatosRegistro {
  nombre: string;
  apellido: string;
  rol: AppRole;
}

/* =========================
   CONTEXTO
========================= */

const ContextoAutenticacion =
  createContext<TipoContextoAutenticacion | undefined>(undefined);

/* =========================
   PROVIDER
========================= */

export function ProveedorAutenticacion({
  children
}: {
  children: React.ReactNode;
}) {
  const [usuario, setUsuario] = useState<User | null>(null);
  const [sesion, setSesion] = useState<Session | null>(null);
  const [perfil, setPerfil] = useState<Profile | null>(null);
  const [rol, setRol] = useState<AppRole | null>(null);
  const [cargando, setCargando] = useState(true);

  const montado = useRef(true);

  /* =========================
     OBTENER PERFIL
  ========================= */

  const obtenerDatosUsuario = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error && error.code === 'PGRST116') {
        // No profile exists — create one from user metadata
        const { data: { user } } = await supabase.auth.getUser();
        if (user && montado.current) {
          const meta = user.user_metadata || {};
          const fullName = meta.full_name || meta.name || '';
          const parts = fullName.split(' ');
          const nombre = meta.nombre || parts[0] || '';
          const apellido = meta.apellido || parts.slice(1).join(' ') || '';

          const { data: newProfile, error: insertErr } = await supabase
            .from('profiles')
            .insert({
              user_id: userId,
              nombre,
              apellido,
              email: user.email || '',
              rol: (meta.rol as AppRole) || 'paciente',
              estado: 'activo',
              avatar_url: meta.avatar_url || meta.picture || null,
            })
            .select()
            .single();

          if (insertErr) {
            console.error('Error al crear perfil:', insertErr);
            return;
          }
          if (newProfile && montado.current) {
            setPerfil(newProfile as Profile);
            setRol(newProfile.rol as AppRole);
          }
        }
        return;
      }

      if (error) {
        console.error('Error al obtener perfil:', error);
        return;
      }

      if (data && montado.current) {
        setPerfil(data as Profile);
        setRol(data.rol as AppRole);
      }
    } catch (error) {
      console.error('Error inesperado:', error);
    }
  }, []);

  const refrescarPerfil = useCallback(async () => {
    if (usuario) {
      await obtenerDatosUsuario(usuario.id);
    }
  }, [usuario, obtenerDatosUsuario]);

  /* =========================
     EFECTO PRINCIPAL
  ========================= */

  useEffect(() => {
    montado.current = true;

    /* ---- Escuchar cambios de sesión ---- */
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((evento, nuevaSesion) => {
      if (!montado.current) return;

      setSesion(nuevaSesion);
      setUsuario(nuevaSesion?.user ?? null);

      if (nuevaSesion?.user) {
        obtenerDatosUsuario(nuevaSesion.user.id);
      } else {
        setPerfil(null);
        setRol(null);
      }

      if (evento === 'SIGNED_OUT') {
        setPerfil(null);
        setRol(null);
      }
    });

    /* ---- Carga inicial (ÚNICO lugar donde se controla "cargando") ---- */
    const inicializarAutenticacion = async () => {
      try {
        const {
          data: { session: sesionExistente }
        } = await supabase.auth.getSession();

        if (!montado.current) return;

        setSesion(sesionExistente);
        setUsuario(sesionExistente?.user ?? null);

        if (sesionExistente?.user) {
          await obtenerDatosUsuario(sesionExistente.user.id);
        }
      } finally {
        if (montado.current) {
          setCargando(false); // ✅ SOLO AQUÍ
        }
      }
    };

    inicializarAutenticacion();

    return () => {
      montado.current = false;
      subscription.unsubscribe();
    };
  }, [obtenerDatosUsuario]);

  /* =========================
     ACCIONES
  ========================= */

  const registrar = async (
    email: string,
    password: string,
    metadata: MetadatosRegistro
  ) => {
    try {
      const redireccion = `${window.location.origin}/`;

      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redireccion,
          data: {
            nombre: metadata.nombre,
            apellido: metadata.apellido,
            rol: metadata.rol
          }
        }
      });

      return { error };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const iniciarSesion = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      return { error };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const cerrarSesion = async () => {
    await supabase.auth.signOut();
    setUsuario(null);
    setSesion(null);
    setPerfil(null);
    setRol(null);
  };

  /* =========================
     PROVIDER
  ========================= */

  return (
    <ContextoAutenticacion.Provider
      value={{
        usuario,
        sesion,
        perfil,
        rol,
        cargando,
        registrar,
        iniciarSesion,
        cerrarSesion,
        refrescarPerfil
      }}
    >
      {children}
    </ContextoAutenticacion.Provider>
  );
}

/* =========================
   HOOK
========================= */

export function usarAutenticacion() {
  const contexto = useContext(ContextoAutenticacion);
  if (!contexto) {
    throw new Error(
      'usarAutenticacion debe usarse dentro de ProveedorAutenticacion'
    );
  }
  return contexto;
}

/* =========================
   ALIASES (English exports for compatibility)
========================= */

// English aliases for backward compatibility
export const AuthProvider = ProveedorAutenticacion;

export function useAuth() {
  const contexto = useContext(ContextoAutenticacion);
  if (!contexto) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  // Return English property names for compatibility
  return {
    user: contexto.usuario,
    session: contexto.sesion,
    profile: contexto.perfil,
    role: contexto.rol,
    loading: contexto.cargando,
    signUp: contexto.registrar,
    signIn: contexto.iniciarSesion,
    signOut: contexto.cerrarSesion,
    refreshProfile: contexto.refrescarPerfil,
  };
}
